import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { POSOrderWithItems } from '../types/admin';

export function useKitchenOrders() {
  const { company } = useAuthStore();

  return useQuery({
    queryKey: ['kitchen-orders', company?.id],
    queryFn: async (): Promise<POSOrderWithItems[]> => {
      if (!company?.id) return [];

      // Get all active orders (pending, confirmed, preparing, ready)
      const { data: orders, error: ordersError } = await supabase
        .from('pos_orders')
        .select('*')
        .eq('company_id', company.id)
        .in('status', ['pending', 'confirmed', 'preparing', 'ready'])
        .order('created_at', { ascending: true });

      if (ordersError) throw ordersError;
      if (!orders || orders.length === 0) return [];

      // Get items for all orders
      const orderIds = orders.map(o => o.id);
      const { data: items, error: itemsError } = await supabase
        .from('pos_order_items')
        .select('*')
        .in('order_id', orderIds);

      if (itemsError) throw itemsError;

      // Group items by order
      return orders.map(order => ({
        ...order,
        items: (items || []).filter(item => item.order_id === order.id),
      })) as POSOrderWithItems[];
    },
    enabled: !!company?.id,
    refetchInterval: 5000, // Refresh every 5 seconds
  });
}
